if (!customElements.get('blako-announcement-bar')) {
  class BlakoAnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slides = [];
      this.currentIndex = 0;
      this.timer = null;
      this.transitionTimer = null;
      this.countdownTimer = null;
      this.pauseReasons = new Set();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.onMouseEnter = () => this.pause('hover');
      this.onMouseLeave = () => this.resume('hover');
      this.onFocusIn = () => this.pause('focus');
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.resume('focus');
      };
      this.onVisibilityChange = () => {
        if (document.hidden) this.pause('visibility');
        else this.resume('visibility');
      };
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
      if (!this.slides.length) return;

      const activeIndex = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.currentIndex = activeIndex;
      this.updateAccessibility();
      this.startCountdowns();

      if (this.dataset.pauseOnHover === 'true') {
        this.addEventListener('mouseenter', this.onMouseEnter);
        this.addEventListener('mouseleave', this.onMouseLeave);
      }
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const selectedIndex = this.slides.findIndex((slide) => slide.contains(event.target) || slide === event.target);
          if (selectedIndex >= 0) this.goTo(selectedIndex, true);
          this.pause('editor');
        });
        this.addEventListener('shopify:block:deselect', () => this.resume('editor'));
      }

      this.play();
    }

    disconnectedCallback() {
      this.clearTimers();
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    get autoplayEnabled() {
      return this.dataset.autoplay === 'true' && this.slides.length > 1;
    }

    get interval() {
      return Math.max(3000, Number(this.dataset.speed || 5) * 1000);
    }

    get transitionDuration() {
      return this.reducedMotion ? 0 : Math.max(0, Number(this.dataset.transitionDuration || 350));
    }

    play() {
      if (!this.autoplayEnabled || this.pauseReasons.size > 0 || document.hidden) return;
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.next(), this.interval);
    }

    pause(reason = 'manual') {
      this.pauseReasons.add(reason);
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    resume(reason = 'manual') {
      this.pauseReasons.delete(reason);
      this.play();
    }

    next() {
      this.goTo((this.currentIndex + 1) % this.slides.length);
    }

    goTo(nextIndex, immediate = false) {
      if (nextIndex === this.currentIndex || !this.slides[nextIndex]) {
        this.play();
        return;
      }

      window.clearTimeout(this.timer);
      window.clearTimeout(this.transitionTimer);

      const currentSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[nextIndex];
      const duration = immediate ? 0 : this.transitionDuration;

      nextSlide.classList.add('is-preparing');
      nextSlide.removeAttribute('inert');
      nextSlide.setAttribute('aria-hidden', 'false');

      // Force the prepared state to render before starting the transition.
      void nextSlide.offsetWidth;

      currentSlide.classList.remove('is-active');
      currentSlide.classList.add('is-leaving');
      nextSlide.classList.remove('is-preparing');
      nextSlide.classList.add('is-active');
      this.currentIndex = nextIndex;

      const finish = () => {
        currentSlide.classList.remove('is-leaving');
        currentSlide.setAttribute('aria-hidden', 'true');
        currentSlide.setAttribute('inert', '');
        this.updateAccessibility();
        this.play();
      };

      if (duration === 0) finish();
      else this.transitionTimer = window.setTimeout(finish, duration + 40);
    }

    updateAccessibility() {
      this.slides.forEach((slide, index) => {
        const isActive = index === this.currentIndex;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (isActive) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
      });
    }

    startCountdowns() {
      const countdowns = Array.from(this.querySelectorAll('[data-countdown-end]'));
      if (!countdowns.length) return;

      const update = () => {
        countdowns.forEach((element) => {
          const endValue = element.dataset.countdownEnd;
          const endTime = Date.parse(endValue);
          const expiredText = element.dataset.expiredText || '';

          if (!endValue || Number.isNaN(endTime)) {
            element.textContent = '';
            return;
          }

          const remaining = endTime - Date.now();
          if (remaining <= 0) {
            element.textContent = expiredText;
            return;
          }

          const totalSeconds = Math.floor(remaining / 1000);
          const days = Math.floor(totalSeconds / 86400);
          const hours = Math.floor((totalSeconds % 86400) / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          const pad = (value) => String(value).padStart(2, '0');

          element.textContent = days > 0
            ? `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`
            : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        });
      };

      update();
      this.countdownTimer = window.setInterval(update, 1000);
    }

    clearTimers() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.transitionTimer);
      window.clearInterval(this.countdownTimer);
    }
  }

  customElements.define('blako-announcement-bar', BlakoAnnouncementBar);
}
